import { useState } from "react";
import { ArrowLeft, ArrowRight, MessageCircle, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { waLink } from "../data/content";

const VENUES = [
  { label: "Restaurant", slug: "restaurant" },
  { label: "Café", slug: "cafe" },
  { label: "Pub / Bar", slug: "pub-bar" },
  { label: "Hotel / Resort", slug: "hotel-resort" },
  { label: "Cloud Kitchen", slug: "cloud-kitchen" },
  { label: "Launching Soon", slug: "launching-soon" },
];
const NEEDS = [
  { label: "More customers", slug: "more-customers" },
  { label: "Social media & ads", slug: "social-media-ads" },
  { label: "Photos & reels", slug: "photos-reels" },
  { label: "Grand launch", slug: "grand-launch" },
  { label: "Branding & uniforms", slug: "branding-uniforms" },
  { label: "Not sure yet", slug: "not-sure-yet" },
];

const Chip = ({ label, selected, onClick, id }) => (
  <button
    onClick={onClick}
    data-testid={id}
    className={`px-3.5 py-2 rounded-full text-xs font-bold border transition-all duration-200 ${
      selected
        ? "bg-aubergine text-cream border-aubergine shadow-md scale-[1.03]"
        : "bg-cream text-ink-muted border-line hover:border-saffron hover:text-aubergine"
    }`}
  >
    {label}
  </button>
);

export const WhatsAppFloat = () => {
  const [open, setOpen] = useState(false);
  const [venue, setVenue] = useState("");
  const [need, setNeed] = useState("");

  const ready = venue && need;
  const message = ready
    ? `Hi GrowMyRestro! I have a ${venue.toLowerCase()} and I need help with ${need.toLowerCase()}. Can we talk?`
    : "";

  const close = () => setOpen(false);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl border border-line shadow-[0_24px_60px_rgba(45,18,77,0.2)] p-6 w-80 sm:w-96"
            data-testid="whatsapp-quick-card"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cardamom font-semibold">
                <span className="w-2 h-2 rounded-full bg-cardamom animate-pulse" /> Online in Bengaluru
              </span>
              <button onClick={close} data-testid="whatsapp-card-close" aria-label="Close" className="text-ink-muted hover:text-ink">
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="font-display font-bold text-aubergine text-lg leading-snug">
              Hi! Tell us two things and we'll take it from there.
            </p>

            <div className="mt-5">
              <p className="text-xs font-bold text-ink-muted uppercase tracking-wider mb-2.5">1. Your venue</p>
              <div className="flex flex-wrap gap-2">
                {VENUES.map((v) => (
                  <Chip key={v.slug} id={`wa-venue-${v.slug}`} label={v.label} selected={venue === v.label} onClick={() => setVenue(v.label)} />
                ))}
              </div>
            </div>

            <div className="mt-5">
              <p className="text-xs font-bold text-ink-muted uppercase tracking-wider mb-2.5">2. You need help with</p>
              <div className="flex flex-wrap gap-2">
                {NEEDS.map((n) => (
                  <Chip key={n.slug} id={`wa-need-${n.slug}`} label={n.label} selected={need === n.label} onClick={() => setNeed(n.label)} />
                ))}
              </div>
            </div>

            <AnimatePresence>
              {ready && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-5 bg-cardamom-sage rounded-2xl p-4">
                    <p className="text-[11px] font-mono uppercase tracking-widest text-cardamom font-semibold mb-1.5">Your message</p>
                    <p className="text-sm text-ink leading-relaxed" data-testid="wa-message-preview">"{message}"</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <a
              href={ready ? waLink(message) : undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!ready}
              data-testid="whatsapp-card-start-chat"
              onClick={(e) => !ready && e.preventDefault()}
              className={`mt-5 flex items-center justify-center gap-2 font-bold text-sm px-4 py-3.5 rounded-full transition-all duration-300 ${
                ready ? "bg-cardamom text-cream hover:bg-aubergine" : "bg-line/60 text-ink-muted cursor-not-allowed"
              }`}
            >
              <MessageCircle className="w-4 h-4" /> Start WhatsApp Chat
              {ready && <ArrowRight className="w-4 h-4" />}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen(!open)}
        data-testid="floating-whatsapp-trigger"
        aria-label="Chat on WhatsApp"
        className="relative w-14 h-14 rounded-full bg-cardamom text-cream flex items-center justify-center shadow-[0_12px_32px_rgba(59,90,64,0.4)] hover:scale-110 transition-transform duration-300"
      >
        <span className="absolute inset-0 rounded-full bg-cardamom animate-ping opacity-20" />
        {open ? <ArrowLeft className="w-6 h-6 relative" /> : <MessageCircle className="w-6 h-6 relative" />}
      </button>
    </div>
  );
};
